// buildViewDefinitionDraft.js

import { normalizeBoardLayout } from 'utilities/BoardLayout';
import { normalizeFilters, normalizeSorts } from './viewModalFilters';
import {
    DEFAULT_SUBTITLE_FIELD_KEY,
    LEGACY_SUBTITLE_FIELD_KEY,
} from 'utilities/NodeSubtitle';

const DEFAULT_CHART_CONFIG = Object.freeze({
    chartType: 'bar',
    aggregation: 'count',
    xField: '',
    yField: '',
    groupField: '',
    showLegend: true,
    showGrid: true,
    fontFamily: 'inherit',
    backgroundColor: '',
    textColor: '',
    gridColor: '',
    yAxisFormat: 'number',
    currencyCode: 'USD',
    xDateBin: 'auto',
    seriesColors: {},
});

const ALLOWED_SCHEDULER_SLOT_INCREMENTS = new Set([15, 30, 60]);
const ALLOWED_TIMELINE_PRESETS = new Set(['timeline', 'gantt']);

function normalizeTimelinePreset(preset) {
    const normalized = String(preset || '').trim().toLowerCase();
    return ALLOWED_TIMELINE_PRESETS.has(normalized) ? normalized : 'timeline';
}

function normalizeChartConfig(chartConfig) {
    const config = chartConfig && typeof chartConfig === 'object' ? chartConfig : {};

    const seriesColors = config?.seriesColors && typeof config.seriesColors === 'object'
        ? Object.entries(config.seriesColors).reduce((acc, [seriesKey, color]) => {
            const key = String(seriesKey || '').trim();
            const value = String(color || '').trim();
            if (!key || !value) return acc;
            acc[key] = value;
            return acc;
        }, {})
        : {};

    return {
        chartType: String(config?.chartType || DEFAULT_CHART_CONFIG.chartType).toLowerCase(),
        aggregation: String(config?.aggregation || DEFAULT_CHART_CONFIG.aggregation).toLowerCase(),
        xField: String(config?.xField || '').trim(),
        yField: String(config?.yField || '').trim(),
        groupField: String(config?.groupField || '').trim(),
        showLegend: config?.showLegend == null ? true : Boolean(config?.showLegend),
        showGrid: config?.showGrid == null ? true : Boolean(config?.showGrid),
        fontFamily: String(config?.fontFamily || DEFAULT_CHART_CONFIG.fontFamily),
        backgroundColor: String(config?.backgroundColor || '').trim(),
        textColor: String(config?.textColor || '').trim(),
        gridColor: String(config?.gridColor || '').trim(),
        yAxisFormat: String(config?.yAxisFormat || DEFAULT_CHART_CONFIG.yAxisFormat).toLowerCase(),
        currencyCode: String(config?.currencyCode || DEFAULT_CHART_CONFIG.currencyCode).trim().toUpperCase(),
        xDateBin: String(config?.xDateBin || DEFAULT_CHART_CONFIG.xDateBin).toLowerCase(),
        seriesColors,
    };
}

function normalizeSchedulerConfig({
    scheduledType,
    scheduledField,
    slotIncrementMinutes,
    scheduledFilters,
    scheduledProperties,
    scheduledFilterFieldMap,
    scheduledTemporalFieldMap,
}) {
    const normalizedScheduledType = String(scheduledType || '').trim();
    const normalizedScheduledField = String(scheduledField || '').trim();
    const normalizedSlotIncrement = Number.parseInt(String(slotIncrementMinutes || '').trim(), 10);

    if (!normalizedScheduledType || !normalizedScheduledField) return null;
    if (!ALLOWED_SCHEDULER_SLOT_INCREMENTS.has(normalizedSlotIncrement)) return null;

    const scheduledFieldDef = scheduledTemporalFieldMap?.get(normalizedScheduledField) || null;
    const scheduledFieldType = String(scheduledFieldDef?.type || '').trim().toLowerCase();
    if (scheduledFieldType !== 'datetimerange') return null;

    const normalizedScheduledFilters = normalizeFilters(
        Array.isArray(scheduledFilters) ? scheduledFilters : [],
        scheduledFilterFieldMap,
    );
    const normalizedScheduledProperties = Array.isArray(scheduledProperties)
        ? [...new Set(scheduledProperties.map((field) => String(field || '').trim()))]
            .filter((field) => field && field !== 'title')
        : [];

    return {
        scheduledType: normalizedScheduledType,
        scheduledField: normalizedScheduledField,
        slotIncrementMinutes: normalizedSlotIncrement,
        scheduledFilters: normalizedScheduledFilters,
        scheduledProperties: normalizedScheduledProperties,
    };
}

export function buildViewDefinitionDraft({
    description,
    title,
    type,
    boardField,
    boardLayout,
    boardEnumOptions,
    viewGroupId,
    properties,
    filters,
    sorts,
    includeDescendants,
    timelineField,
    timelineRowMode,
    timelineGroupField,
    showUnscheduled,
    preset,
    showHierarchy,
    showDependencies,
    allowScheduleEdit,
    calendarField,
    chartConfig,
    scheduledType,
    scheduledField,
    slotIncrementMinutes,
    scheduledFilters,
    scheduledProperties,
    isGalleryDataType,
    filterFieldMap,
    scheduledFilterFieldMap,
    scheduledTemporalFieldMap,
}) {
    const trimmedDescription = String(description || '').trim();
    const trimmedTitle = String(title || '').trim();
    if (!trimmedTitle) return null;

    const normalizedType = String(type || 'LIST').trim().toUpperCase() || 'LIST';
    if (normalizedType === 'TIMELINE' && !timelineField) return null;

    const normalizedChartConfig = normalizeChartConfig(chartConfig);
    const chartNeedsMeasure = normalizedChartConfig.aggregation !== 'count';
    if (normalizedType === 'CHART' && !normalizedChartConfig.xField) return null;
    if (normalizedType === 'CHART' && chartNeedsMeasure && !normalizedChartConfig.yField) return null;

    const normalizedSchedulerConfig = normalizedType === 'SCHEDULER'
        ? normalizeSchedulerConfig({
            scheduledType,
            scheduledField,
            slotIncrementMinutes,
            scheduledFilters,
            scheduledProperties,
            scheduledFilterFieldMap,
            scheduledTemporalFieldMap,
        })
        : null;
    if (normalizedType === 'SCHEDULER' && !normalizedSchedulerConfig) return null;

    const normalizedFilters = normalizeFilters(Array.isArray(filters) ? filters : [], filterFieldMap);
    const normalizedSorts = normalizeSorts(Array.isArray(sorts) ? sorts : []);
    const normalizedBoardEnumOptions = normalizedType === 'BOARD' && boardField
        ? {
            fieldKey: String(boardField || '').trim(),
            optionOrderIds: Array.isArray(boardEnumOptions?.optionOrderIds)
                ? [...new Set(boardEnumOptions.optionOrderIds
                    .map((value) => String(value || '').trim())
                    .filter(Boolean))]
                : [],
            sortMode: String(boardEnumOptions?.sortMode || 'inherit').trim().toLowerCase() || 'inherit',
            hiddenOptionIds: [],
        }
        : null;
    const normalizedProperties = Array.isArray(properties)
        ? [...new Set(properties.map((field) => String(field || '').trim()))]
            .filter((field) => field && field !== 'title')
        : [];
    const shouldStoreProperties = normalizedType === 'BOARD'
        || normalizedType === 'LIST'
        || normalizedType === 'ALPHA_LIST'
        || normalizedType === 'TABLE'
        || isGalleryDataType;
    const storedProperties = shouldStoreProperties ? normalizedProperties : [];
    const includesSubtitle = storedProperties.includes(DEFAULT_SUBTITLE_FIELD_KEY)
        || storedProperties.includes(LEGACY_SUBTITLE_FIELD_KEY);

    return {
        description: trimmedDescription || null,
        title: trimmedTitle,
        type: normalizedType,
        boardField: normalizedType === 'BOARD' ? (boardField || null) : null,
        boardLayout: normalizedType === 'BOARD' ? normalizeBoardLayout(boardLayout) : null,
        boardEnumOptions: normalizedBoardEnumOptions,
        viewGroupId: String(viewGroupId || '').trim() || null,
        properties: storedProperties.length ? storedProperties : null,
        filters: normalizedFilters.length ? normalizedFilters : null,
        sorts: normalizedSorts.length ? normalizedSorts : null,
        includeDescendants: Boolean(includeDescendants),
        includeSubtitle: includesSubtitle,
        includeCover: storedProperties.includes('cover'),
        timelineField: normalizedType === 'TIMELINE' ? (timelineField || null) : null,
        timelineRowMode: normalizedType === 'TIMELINE' ? (timelineRowMode || 'flat') : null,
        timelineGroupField: normalizedType === 'TIMELINE' && timelineRowMode === 'grouped'
            ? (timelineGroupField || null)
            : null,
        showUnscheduled: normalizedType === 'TIMELINE'
            ? Boolean(showUnscheduled)
            : null,
        preset: normalizedType === 'TIMELINE'
            ? normalizeTimelinePreset(preset)
            : null,
        showHierarchy: normalizedType === 'TIMELINE' ? Boolean(showHierarchy) : null,
        showDependencies: normalizedType === 'TIMELINE' ? Boolean(showDependencies) : null,
        allowScheduleEdit: normalizedType === 'TIMELINE' ? Boolean(allowScheduleEdit) : null,
        calendarField: normalizedType === 'CALENDAR'
          ? (String(calendarField || '').trim() || null)
          : null,
        chartConfig: normalizedType === 'CHART' ? normalizedChartConfig : null,
        schedulerConfig: normalizedType === 'SCHEDULER' ? normalizedSchedulerConfig : null,
    };
}